import SelectInput from '../../Utils/FormInputs/SelectInput';
import TextField from '../../Utils/FormInputs/TextField';
/**
 * DrugLog.js
 * Drug administration log during surgery. Tracks all drugs given intra-operatively
 * with auto-timestamped entries, route selection, and running list.
 */
import React, { useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import Chip from '@material-ui/core/Chip';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { surgeryService } from '../../../services/surgeryService';

const ROUTES = [
  { value: 'IV', label: 'IV (Intravenous)' },
  { value: 'IM', label: 'IM (Intramuscular)' },
  { value: 'SC', label: 'SC (Subcutaneous)' },
  { value: 'PO', label: 'PO (Oral)' },
  { value: 'Topical', label: 'Topical' },
  { value: 'Inhalation', label: 'Inhalation' },
];

const ROUTE_COLORS = {
  IV: '#1976D2',
  IM: '#7B1FA2',
  SC: '#388E3C',
  PO: '#F57C00',
  Topical: '#00796B',
  Inhalation: '#5D4037',
};

const useStyles = makeStyles(() => ({
  root: {
    padding: 16,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#333',
  },
  addForm: {
    padding: 16,
    marginBottom: 16,
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    border: '1px solid #E0E0E0',
  },
  addFormTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: 12,
  },
  tableContainer: {
    '& .MuiTableCell-root': {
      padding: '10px 12px',
      fontSize: '0.85rem',
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#666',
      backgroundColor: '#FAFAFA',
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
  },
  routeChip: {
    fontSize: '0.7rem',
    fontWeight: 600,
    height: 24,
  },
  emptyState: {
    textAlign: 'center',
    padding: '24px 16px',
    color: '#999',
  },
  drugCount: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: 8,
    textAlign: 'right',
  },
  timestamp: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    color: '#333',
  },
}));

const formatTimestamp = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const DrugLog = ({ surgeryId }) => {
  const classes = useStyles();

  const [drugs, setDrugs] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);

  // Add form fields
  const [drugName, setDrugName] = useState('');
  const [dose, setDose] = useState('');
  const [route, setRoute] = useState('IV');
  const [givenBy, setGivenBy] = useState('');

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const resetForm = () => {
    setDrugName('');
    setDose('');
    setRoute('IV');
    setGivenBy('');
  };

  const handleAddDrug = useCallback(async () => {
    if (!drugName.trim() || !dose.trim()) {
      setSnackbar({ open: true, message: 'Drug name and dose are required', severity: 'warning' });
      return;
    }

    const newDrug = {
      id: Date.now(),
      timestamp: formatTimestamp(),
      drug_name: drugName.trim(),
      dose: dose.trim(),
      route,
      given_by: givenBy.trim() || 'Anaesthetist',
    };

    setSaving(true);
    try {
      await surgeryService.addDrug({
        surgery_id: surgeryId,
        ...newDrug,
      });

      setDrugs((prev) => [...prev, newDrug]);
      resetForm();
      setShowAddForm(false);
      setSnackbar({ open: true, message: 'Drug recorded successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to record drug', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [drugName, dose, route, givenBy, surgeryId]);

  const handleDeleteDrug = useCallback((drugId) => {
    setDrugs((prev) => prev.filter((d) => d.id !== drugId));
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && drugName.trim() && dose.trim()) {
      handleAddDrug();
    }
  };

  return (
    <Paper className={classes.root} elevation={0} variant="outlined">
      <div className={classes.header}>
        <Typography className={classes.title}>Drug Administration Log</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setShowAddForm(true)}
          disabled={showAddForm}
        >
          Add Drug
        </Button>
      </div>

      {/* Add drug form */}
      {showAddForm && (
        <div className={classes.addForm}>
          <Typography className={classes.addFormTitle}>Record Drug Administration</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Drug Name"
                value={drugName}
                onChange={(e) => setDrugName(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="e.g. Morphine"
                autoFocus
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Dose"
                value={dose}
                onChange={(e) => setDose(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="e.g. 5mg"
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <SelectInput
                name="route"
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                options={ROUTES.map((r) => ({ value: r.value, text: r.label }))}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Given By"
                value={givenBy}
                onChange={(e) => setGivenBy(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="e.g. Dr. Kizza"
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <div style={{ display: 'flex', gap: 8, height: '100%', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleAddDrug}
                  disabled={saving || !drugName.trim() || !dose.trim()}
                >
                  {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    resetForm();
                    setShowAddForm(false);
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </Grid>
          </Grid>
        </div>
      )}

      {/* Drug table */}
      {drugs.length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body2" color="textSecondary">
            No drugs administered yet. Click &quot;Add Drug&quot; to begin logging.
          </Typography>
        </div>
      ) : (
        <>
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Drug Name</TableCell>
                  <TableCell>Dose</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Given By</TableCell>
                  <TableCell style={{ width: 48 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drugs.map((drug) => (
                  <TableRow key={drug.id} hover>
                    <TableCell>
                      <span className={classes.timestamp}>{drug.timestamp}</span>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {drug.drug_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{drug.dose}</TableCell>
                    <TableCell>
                      <Chip
                        label={drug.route}
                        size="small"
                        className={classes.routeChip}
                        style={{
                          backgroundColor: ROUTE_COLORS[drug.route] || '#757575',
                          color: '#fff',
                        }}
                      />
                    </TableCell>
                    <TableCell>{drug.given_by}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDrug(drug.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography className={classes.drugCount}>
            {drugs.length} drug{drugs.length !== 1 ? 's' : ''} administered
          </Typography>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default DrugLog;
